import { useMemo, type ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import useCharterBundlePricing from 'hooks/CharterFlights/useCharterBundlePricing'
import type { CharterBundlePricingInput } from 'hooks/CharterFlights/useCharterBundlePricing'
import { FlightsClassTypes, isRewardFareClass, toBaseFareClass } from 'constants/flight'
import { useMaldivesDirectBundleContext } from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectBundleContext'
import { useCharterFlightPrice } from 'hooks/useFlightPrice'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCharterBundleRoute } from 'selectors/offerPage/initialOfferPageStateSelectors'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import LuxPlusLogoWrapper from 'luxPlus/components/LuxPlusLogoWrapper'
import { computeRewardBundlePrice, getRewardPriceDisplayState, REWARD_PREVIEW_RATE_BY_CABIN } from 'lib/maldivesCharter/classicRewardPricing'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { isConfiguredFeederOriginAirportCode } from 'lib/charterFlights/charterBundleOfferPageUtils'

interface Props {
  offer: App.Offer | App.OfferSummary
  pricing: CharterBundlePricingInput
  isPricePerPerson?: boolean
  nights?: number
  filters?: App.OfferListFilters
  hideIncludesTaxesAndFees?: boolean
}

const defaultRooms: Array<App.Occupants> = [{ adults: 2 }]

function CharterBundlePricing({ offer, pricing, isPricePerPerson, nights, filters, hideIncludesTaxesAndFees = false }: Props) {
  const intl = useIntl()
  const {
    displayPrice,
    memberDisplayPrice,
    valuePrice,
    discountPercentage: resolvedDiscountPercentage,
    guestCount,
    showMemberPricing,
  } = useCharterBundlePricing(pricing, isPricePerPerson)

  const { selectedFareClass, hasUserSelectedDates, hasFeederFlightPriced } = useMaldivesDirectBundleContext()
  const isRewardSelected = !!selectedFareClass && isRewardFareClass(selectedFareClass)
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const rewardLockedForLogin = isRewardSelected && !memberId
  const charterBundleRoute = useAppSelector(state => getCharterBundleRoute(state, offer))
  const rooms = filters?.rooms ?? defaultRooms
  const { totalPrice: charterFlightPrice, rewardSeatPointsTotal, rewardSeatCashCoPayTotal, rewardAvailable } = useCharterFlightPrice({
    charterBundleRoute: charterBundleRoute ?? 'melbourne-maldives',
    checkInDate: filters?.checkIn,
    checkOutDate: filters?.checkOut,
    occupancies: rooms,
    fareClass: isRewardSelected && selectedFareClass ? selectedFareClass : FlightsClassTypes.ECONOMY,
    disabled: !isRewardSelected || !charterBundleRoute,
  })

  const rewardPrice = getRewardPriceDisplayState({ rewardAvailable, rewardSeatPointsTotal, rewardSeatCashCoPayTotal })
  const showRewardPrice = isRewardSelected && rewardPrice.show
  const rewardNeedsDates = isRewardSelected && !hasUserSelectedDates
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const needsPreview = isRewardSelected && (rewardLockedForLogin || !hasLoyaltyAccount || rewardNeedsDates)

  const priceElement = useMemo(() => {
    if (showRewardPrice) {
      const rewardBundlePrice = computeRewardBundlePrice({
        hotelPrice: (pricing.price ?? 0) - (charterFlightPrice ?? 0),
        hotelMemberPrice: pricing.memberPrice ? pricing.memberPrice - (charterFlightPrice ?? 0) : undefined,
        rewardPointsTotal: rewardSeatPointsTotal!,
        rewardCashCoPayTotal: rewardSeatCashCoPayTotal!,
      })
      const isMemberRewardPrice = rewardBundlePrice.memberCash !== undefined
      const divisor = isPricePerPerson && guestCount > 0 ? guestCount : 1
      const rewardPointsDisplay = rewardBundlePrice.points / divisor
      const rewardCashTotal = (rewardBundlePrice.memberCash ?? rewardBundlePrice.cash) / divisor
      const nonMemberRewardCashTotal = rewardBundlePrice.cash / divisor
      return (
        <VerticalSpacer gap={4}>
          <Group direction="horizontal" gap={4} verticalAlign="baseline" wrap="wrap" data-testid="charter-bundle-pricing-reward">
            <Heading variant="heading4" colour={isMemberRewardPrice ? 'lux-plus' : undefined}>
              <FormattedMessage
                id="HaCvr3bzUcX91PNWgOpJ"
                defaultMessage="{points} + {cash}"
                description="Reward-seat bundle price on offer tiles, showing the loyalty points portion and the cash co-pay portion combined as one heading."
                values={{
                  points: <FormatLoyaltyPoints points={rewardPointsDisplay} abbreviated />,
                  cash: <FormatCurrency value={rewardCashTotal} format="roundedDollar" />,
                }}
              />
            </Heading>
            <Caption variant="medium" colour={isMemberRewardPrice ? 'lux-plus' : 'neutral-three'} weight="normal" format="lowercase">
              {isPricePerPerson
                ? intl.formatMessage({ id: 'qrqVRF4q4TMPW7pvEHiC', defaultMessage: 'person', description: 'Per-person pricing unit suffix for reward bundle price on search tile' })
                : intl.formatMessage({ id: 'j--PuUYLTgWUxAxROh0Z', defaultMessage: 'total', description: 'Total pricing unit suffix in the Maldives calendar' })}
            </Caption>
            {isMemberRewardPrice && <Caption variant="medium" colour="lux-plus" weight="normal" format="lowercase" wrap="no-wrap">
              <FormattedMessage
                id="qg18mjM_yahXRtEZVoVD"
                defaultMessage="with <luxPlusLogo></luxPlusLogo>"
                description="Caption suffix appended after a LuxPlus member price that reads with the LuxPlus logo"
                values={{ luxPlusLogo: () => <LuxPlusLogoWrapper height={8} colour="inherit" /> }}
              />
            </Caption>}
          </Group>
          {isMemberRewardPrice && (
            <Caption variant="medium" colour="neutral-two">
              <FormattedMessage
                id="v9wxhoHxiRp0_D8CX8vh"
                defaultMessage="Non-member <b>{points} + {cash}</b>"
                description="Non-member reward price shown below the LuxPlus reward price on search tiles"
                values={{
                  points: <FormatLoyaltyPoints points={rewardPointsDisplay} abbreviated />,
                  cash: <FormatCurrency value={nonMemberRewardCashTotal} format="roundedDollar" />,
                  b: (chunks: ReactNode) => <b>{chunks}</b>,
                }}
              />
            </Caption>
          )}
        </VerticalSpacer>
      )
    }
    if (needsPreview) {
      const baseCabin = selectedFareClass ? toBaseFareClass(selectedFareClass) : undefined
      const rate = baseCabin ? REWARD_PREVIEW_RATE_BY_CABIN[baseCabin] : undefined
      // Use same hotel derivation as the real reward path: bundle total - flight
      const hotelPrice = (pricing.price ?? 0) - (charterFlightPrice ?? 0)
      const hotelMemberPrice = pricing.memberPrice ? pricing.memberPrice - (charterFlightPrice ?? 0) : undefined
      if (rate && hotelPrice > 0) {
        const totalPassengers = sumUpOccupancies(rooms)
        const divisor = isPricePerPerson && totalPassengers > 0 ? totalPassengers : 1
        const previewPoints = (rate.pointsPerPerson * totalPassengers) / divisor
        const previewCash = (hotelPrice + rate.cashPerPerson * totalPassengers) / divisor
        const previewMemberCash = hotelMemberPrice ? (hotelMemberPrice + rate.cashPerPerson * totalPassengers) / divisor : undefined
        const isMember = previewMemberCash !== undefined
        const cashDisplay = previewMemberCash ?? previewCash
        return (
          <VerticalSpacer gap={4}>
            <Group direction="horizontal" gap={4} verticalAlign="baseline" wrap="wrap">
              <Heading variant="heading4" colour={isMember ? 'lux-plus' : undefined}>
                <FormattedMessage
                  id="HaCvr3bzUcX91PNWgOpJ"
                  defaultMessage="{points} + {cash}"
                  description="Reward-seat bundle price on offer tiles, showing the loyalty points portion and the cash co-pay portion combined as one heading."
                  values={{
                    points: <FormatLoyaltyPoints points={previewPoints} abbreviated />,
                    cash: <FormatCurrency value={cashDisplay} format="roundedDollar" />,
                  }}
                />
              </Heading>
              <Caption variant="medium" colour={isMember ? 'lux-plus' : 'neutral-three'} weight="normal" format="lowercase">
                {isPricePerPerson
                  ? intl.formatMessage({ id: 'qrqVRF4q4TMPW7pvEHiC', defaultMessage: 'person', description: 'Per-person pricing unit suffix for reward bundle price on search tile' })
                  : intl.formatMessage({ id: 'j--PuUYLTgWUxAxROh0Z', defaultMessage: 'total', description: 'Total pricing unit suffix in the Maldives calendar' })}
              </Caption>
            </Group>
            {isMember && (
              <Caption variant="medium" colour="neutral-two">
                <FormattedMessage
                  id="v9wxhoHxiRp0_D8CX8vh"
                  defaultMessage="Non-member <b>{points} + {cash}</b>"
                  description="Non-member reward price shown below the LuxPlus reward price on search tiles"
                  values={{
                    points: <FormatLoyaltyPoints points={previewPoints} abbreviated />,
                    cash: <FormatCurrency value={previewCash} format="roundedDollar" />,
                    b: (chunks: ReactNode) => <b>{chunks}</b>,
                  }}
                />
              </Caption>
            )}
          </VerticalSpacer>
        )
      }
    }
    if (showMemberPricing) {
      return (
        <LuxPlusPriceStack
          price={displayPrice ?? 0}
          memberPrice={memberDisplayPrice}
          saleUnit={isPricePerPerson ? 'person' : 'total'}
          size="L"
        />
      )
    }
    return (
      <PriceRowPrice
        size="L"
        price={displayPrice ?? 0}
        saleUnit={isPricePerPerson ? 'person' : undefined}
      />
    )
  }, [showRewardPrice, needsPreview, selectedFareClass, showMemberPricing, displayPrice, memberDisplayPrice, isPricePerPerson, rewardSeatPointsTotal, rewardSeatCashCoPayTotal, pricing.price, pricing.memberPrice, charterFlightPrice, intl, guestCount, rooms])

  const roomCount = pricing.roomCount ?? 1
  const roomCaption = isPricePerPerson
    ? intl.formatMessage(
        {
          id: 'Uv5Ws_kAoNFVhFWlWnig',
          defaultMessage: '{guestCount, plural, one {# person} other {# people}}',
          description: 'Guest count caption for charter bundle pricing per person',
        },
        { guestCount },
      )
    : intl.formatMessage(
        {
          id: '94mAZcswvt-huk62Hj67',
          defaultMessage: '{roomCount, plural, one {# room} other {# rooms}}',
          description: 'Room count caption for charter bundle pricing total',
        },
        { roomCount },
      )

  // TODO: i18n — complex pricing caption with trailing ' from' and ' & flights' fragments
  const feederAirportCode = isConfiguredFeederOriginAirportCode(
    charterBundleRoute ?? 'melbourne-maldives',
    filters?.feederOriginAirportCode,
  )
    ? filters?.feederOriginAirportCode
    : undefined
  // Once dates are picked, only show the feeder caption when at least one leg actually
  // carries a `feederEstimatedPrice`. Before dates are picked we keep the configured-feeder
  // caption (so the tile still advertises the feeder origin upfront).
  const isFeederIncluded = !!feederAirportCode && (hasUserSelectedDates ? !!hasFeederFlightPriced : true)
  let nightsAndFlightsFromLabel: string | undefined
  if (nights) {
    if (isFeederIncluded) {
      nightsAndFlightsFromLabel = intl.formatMessage(
        {
          id: 'RZdbIaHFEoEvUKtdO5O7',
          defaultMessage: '{nights, plural, one {# night} other {# nights}} & {code} return flights from',
          description: 'Charter bundle price caption showing nights and feeder airport code for non-Melbourne departures',
        },
        { nights, code: feederAirportCode },
      )
    } else {
      nightsAndFlightsFromLabel = intl.formatMessage(
        {
          id: 'FyZfb0XtR5FizQdNr4_Q',
          defaultMessage: '{resolvedNights, plural, one {# night} other {# nights}} & flights from',
          description: 'Charter bundle price caption showing nights and flights',
        },
        { resolvedNights: nights },
      )
    }
  }
  const fromLabel = intl.formatMessage({
    id: 'JSMgIzowLxrZt6jlUcqy',
    defaultMessage: 'From',
    description: 'Charter bundle price caption prefix for per-person pricing',
  })
  const totalFromLabel = intl.formatMessage({
    id: '-J1OGWHAvDhuHC2juRhD',
    defaultMessage: 'Total from',
    description: 'Charter bundle price caption prefix for total pricing',
  })
  const priceCaption = nightsAndFlightsFromLabel ?? (isPricePerPerson ? fromLabel : totalFromLabel)

  return (
    <Group direction="vertical" gap={4} fullWidth>
      <PriceRowPriceCaption>
        {priceCaption}
      </PriceRowPriceCaption>
      {priceElement}
      {!!valuePrice && !!resolvedDiscountPercentage && resolvedDiscountPercentage > 0 && (
        <PriceRowValueDiscountWithCaption
          size="M"
          originalValue={valuePrice}
          discountPercentage={resolvedDiscountPercentage}
        />
      )}
      <PriceRowCaption>{roomCaption}</PriceRowCaption>
      {!hideIncludesTaxesAndFees && <PriceRowTaxesAndFees />}
      <PriceRowAgentHubCommission
        productType={offer.productType}
        offerId={offer.id}
      />
    </Group>
  )
}

export default CharterBundlePricing
